import { ChangeDetectorRef, Component,
  inject} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { MatDialog } from "@angular/material/dialog";
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterModule,
} from "@angular/router";
import { CommonModule } from "@angular/common";
import { ToastComponent } from "~/dash/core/toast/toast.component";
import { TranslateModule } from "@ngx-translate/core";

import { StorageService } from "./core/services/storage.service";
import { ThemeService } from "./core/services/theme.service";
import { PrintingService } from "./core/services/printing.service";
import { CompanyService } from "./core/services/company.service";
import { AuthenticationService } from "./core/services/authentication.service";
import { UtilsService } from "./core/helpers/utils";
import { LogService } from "./core/services/log.service";
import { Company } from "./core/models/company";
import { NavigationService } from "./core/services/navigation.service";

import { StLoadingService } from "~/dash/core/st-loading.service";
import { FormatOddService } from "~/core/services/format-odd.service";
import { HelperPlus2Service } from "~/core/services/helper-plus2.service";
import { ResourcesService } from "~/core/services/resources.service";
import { language } from "~/core/constants/constants";
import { DestroyService } from '~/core/services/destroy.service';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss", "../styles/themes.scss"],
  imports: [CommonModule, RouterModule, ToastComponent, TranslateModule],
  providers: [DestroyService],
})
export class AppComponent {
  private readonly destroy$ = inject(DestroyService);

  companyFriendlyName: string;
  companyLogo: string;
  enableComponents: boolean;

  constructor(
    public translate: TranslateService,
    public storageService: StorageService,
    public companyService: CompanyService,
    private themeService: ThemeService,
    public printingService: PrintingService,
    public auth: AuthenticationService,
    public dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private navigation: NavigationService,
    protected router: Router,
    public readonly formatOddService: FormatOddService,
    protected helper: HelperPlus2Service,
  ) {
    this.showComponents(false);
    const browserPath =
      typeof location !== "undefined"
        ? `${location.pathname}${location.search}`
        : "";
    if (
      this.isDeployInfoUrl(this.router.url) ||
      this.isDeployInfoUrl(browserPath)
    ) {
      this.unlockDeployInfoShell();
    }
    this.updateCompanyDisplayedData(CompanyService.currentCompany);
    this.companyInfo();
    this.auth.loginEvent.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (!this.auth.isLoggedIn()) return;
      this.companyService.authInfo().pipe(takeUntil(this.destroy$)).subscribe((item) => {
        if (item?.company) {
          this.applyCompanyInfo(item);
        }
      });
    });
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (
        event instanceof NavigationStart &&
        event.url?.includes("back-office")
      ) {
        StLoadingService.show();
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        StLoadingService.hide();
      }
      if (
        event instanceof NavigationEnd &&
        this.isDeployInfoUrl(event.urlAfterRedirects)
      ) {
        this.unlockDeployInfoShell();
      }
    });
  }

  /** Rota pública de metadados de build — não depende de company/info nem do router-outlet condicional. */
  private isDeployInfoUrl(url: string): boolean {
    return url.includes("deploy-info");
  }

  private unlockDeployInfoShell(): void {
    this.showComponents(true);
    const el = document.getElementById("app-component-view");
    if (el) {
      el.style.display = "block";
    }
    this.cdr.markForCheck();
  }

  companyInfo() {
    this.companyService.info().pipe(takeUntil(this.destroy$)).subscribe({
      next: (item) => {
        this.applyCompanyInfo(item, true);
      },
      error: () => {
        LogService.log("Error to Update company!");
        this.themeService.setTheme(1);
      },
    });
  }

  /**
   * Applies company/config from info response to app state and UI.
   * @param item - response from /info or /auth-info (company, config, view_config, etc.)
   * @param isInitialLoad - if true, also shows components, prepares menu (bootstrap), and runs userUpdate (validateToken).
   *                       When false (e.g. after login), we skip userUpdate to avoid loop: it calls validateToken which emits loginEvent again.
   */
  private applyCompanyInfo(item: any, isInitialLoad = false) {
    const company = item?.company;
    if (!company) return;
    ResourcesService.loadDependences(company);
    this.companyService.setCurrentCompany(item);
    this.themeService.customTheme();
    this.updateCompanyDisplayedData(company);
    this.changeFavicon(company.favicon);
    this.setLn();
    if (isInitialLoad) {
      this.userUpdate();
      this.showComponents(true);
      const el = document.getElementById("app-component-view");
      if (el) el.style.display = "block";
    }
    this.cdr.detectChanges();
  }

  showComponents(value: boolean) {
    this.enableComponents = value;
  }

  userUpdate() {
    const hasToken = window.location.search.indexOf("?token=");

    if (hasToken > -1) {
      const token = window.location.search.split("?token=")[1];
      this.auth.storeToken(token);
      this.navigation.closeRequestDeposit();
    }

    this.auth.validateToken();
  }

  setLn() {
    const saveLanguage = (lang: string) => {
      this.translate.setDefaultLang(lang);
      this.translate.use(lang);
      StorageService.currentLanguage = lang;
    };

    const languageFromCompany = () => {
      const companyLangKey = CompanyService.currentCompany?.language;
      return language[companyLangKey as keyof typeof language];
    };

    saveLanguage(
      this.storageService.getLanguage() ||
        languageFromCompany() ||
        UtilsService.getBrowserLanguage() ||
        language.PT_BR,
    );
  }

  changeFavicon(src) {
    let link = document.createElement("link");
    if (document.querySelector("link[rel*='icon']")) {
      link = document.querySelector("link[rel*='icon']");
    }
    const oldLink = document.getElementById("dynamic-favicon");
    link.id = "dynamic-favicon";
    link.rel = "icon";
    link.href = src;
    if (oldLink) {
      document.head.removeChild(oldLink);
    }
    document.head.appendChild(link);
  }

  updateCompanyDisplayedData(company: Company) {
    this.companyLogo = CompanyService.getLogo();
    this.companyFriendlyName = company?.friendly_name;
  }
}
